import React, { createContext, useState, useContext, useEffect } from 'react';

// Date format options
export const DATE_FORMATS = {
  SHAMSI: 'shamsi',
  QAMARI: 'qamari',
  GREGORIAN: 'gregorian'
};

// Valid format check
const isValidFormat = (format) => {
  return Object.values(DATE_FORMATS).includes(format);
};

// Migration function to handle old "hijri" values
const migrateDateFormat = (format) => {
  if (format === 'hijri') {
    console.log('Migrating old "hijri" format to "qamari"');
    return DATE_FORMATS.QAMARI;
  }
  return format;
};

// Create context
export const DateFormatContext = createContext({
  dateFormat: DATE_FORMATS.GREGORIAN, // Default to Gregorian, or your preferred default
  setDateFormat: () => {},
});

// Custom hook to use the date format context
export const useDateFormat = () => useContext(DateFormatContext);

// Date format provider component
export const DateFormatProvider = ({ children }) => {
  // Get stored date format preference or default to Qamari (Hijri)
  const getInitialFormat = () => {
    try {
      const storedFormat = localStorage.getItem('dateFormat');
      if (storedFormat) {
        // Migrate old "hijri" values to "qamari"
        const migratedFormat = migrateDateFormat(storedFormat);
        if (isValidFormat(migratedFormat)) {
          console.log('Using stored date format:', migratedFormat);
          // Update localStorage with migrated value
          if (migratedFormat !== storedFormat) {
            localStorage.setItem('dateFormat', migratedFormat);
          }
          return migratedFormat;
        }
      }
    } catch (error) {
      console.error('Error reading date format from localStorage:', error);
    }
    console.log(`Using default date format: ${DATE_FORMATS.QAMARI}`);
    return DATE_FORMATS.QAMARI; // Default to Qamari (Hijri) if nothing valid is stored
  };
  
  const [dateFormat, setDateFormatState] = useState(getInitialFormat);
  
  // Update date format and store in localStorage
  const setDateFormat = (format) => {
    console.log('Setting date format to:', format);
    if (!isValidFormat(format)) {
      console.error('Invalid date format specified:', format);
      return;
    }
    
    setDateFormatState(format);
    try {
      localStorage.setItem('dateFormat', format);
    } catch (error) {
      console.error('Error saving date format to localStorage:', error);
    }
  };

  // Context value
  const contextValue = {
    dateFormat,
    setDateFormat,
  };

  return (
    <DateFormatContext.Provider value={contextValue}>
      {children}
    </DateFormatContext.Provider>
  );
};

export default DateFormatProvider; 